import { Sparkles, Camera } from "lucide-react";
import { useState, useEffect, useRef, type ChangeEvent, type CSSProperties, type ReactNode } from "react";
import SectionReveal from "./SectionReveal";
import SectionBanner from "./SectionBanner";
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import jessicaIcon from "../assets/jessica-icon.png.asset.json";
import { foreground, muted, accent, border, body, display } from "./tokens";
import type { ReportHostedSectionProps } from '../reportHostedSection.types';

const needs = [
  "Behavioral health support",
  "Legal aid coordination",
  "Housing assistance",
  "Financial stability support",
];

const ongoingPriorities = [
  "Continue trauma-focused telehealth therapy",
  "Maintain progress toward housing and financial stability",
];

const challenge = [
  "Recent domestic violence survivor suddenly navigating single parenthood",
  "Facing profound housing instability and significant financial strain",
  "Overwhelming legal process of divorce compounding acute stress",
  "Managing pre-existing depression and ADHD while working multiple jobs",
];

const howReema = [
  "Community Guide Joel built trust by helping update her medical ID after her name change",
  "Researched legal aid resources to support her divorce proceedings",
  "Sent housing assistance information to address her housing crisis",
  "Helped her find and apply for higher-paying jobs to stabilize finances",
];

const outcomes = [
  "Accepted referral to Reema’s telehealth therapy program for trauma-focused support",
  "Successfully connected with legal aid to manage her divorce",
  "Achieved greater life stability across housing, finances, and legal issues",
  "Engaging more consistently in mental health care and building toward independence",
];

const ICON_STORAGE_KEY = "reema-member-spotlight:icon";

const MemberIcon = ({ alt, className }: { alt: string; className?: string }) => {
  const [src, setSrc] = useState<string>(jessicaIcon.url);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(ICON_STORAGE_KEY);
      if (saved) setSrc(saved);
    } catch {
      // localStorage unavailable — fall back to bundled default.
    }
  }, []);

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setSrc(dataUrl);
      try {
        localStorage.setItem(ICON_STORAGE_KEY, dataUrl);
      } catch {
        // Persisting is best-effort; ignore quota/availability errors.
      }
    };
    reader.onerror = () => {
      // File read failed; leave existing icon in place.
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className={`relative group ${className ?? ""}`}>
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover bg-white rounded-full"
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 group-hover:bg-black/30 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reema-coral-100)] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
        aria-label="Change member photo"
        title="Change member photo"
      >
        <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
        aria-hidden="true"
      />
    </div>
  );
};

const Editable = ({
  noteKey,
  children,
  as: As = "span",
  style,
  className,
}: {
  noteKey: string;
  children: ReactNode;
  as?: any;
  style?: CSSProperties;
  className?: string;
}) => (
  <As
    contentEditable
    suppressContentEditableWarning
    data-editable-note={`member-spotlight:${noteKey}`}
    spellCheck={false}
    className={className}
    style={{ outline: "none", ...style }}
  >
    {children}
  </As>
);

const Eyebrow = ({ children, noteKey }: { children: ReactNode; noteKey: string }) => (
  <Editable
    noteKey={noteKey}
    as="p"
    className="uppercase mb-3"
    style={{ color: accent, fontWeight: 700, fontSize: 12, letterSpacing: "0.18em" }}
  >
    {children}
  </Editable>
);

const EditableBulletList = ({
  items,
  groupKey,
  noWrap,
}: {
  items: string[];
  groupKey: string;
  noWrap?: boolean;
}) => (
  <ul className="space-y-1.5" style={{ color: muted, fontFamily: body, fontSize: 15, lineHeight: 1.5 }}>
    {items.map((item, i) => (
      <li key={`${groupKey}-${i}`} className="flex gap-3">
        <span
          className="mt-2 w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: accent }}
          aria-hidden="true"
        />
        <Editable noteKey={`${groupKey}:${i}`} className={noWrap ? "whitespace-nowrap" : undefined}>
          {item}
        </Editable>
      </li>
    ))}
  </ul>
);

const SubHeading = ({ children, noteKey }: { children: ReactNode; noteKey: string }) => (
  <Editable
    noteKey={noteKey}
    as="h3"
    style={{
      fontFamily: display,
      fontWeight: 600,
      fontSize: 20,
      color: foreground,
      margin: "0 0 8px",
      lineHeight: 1.25,
      letterSpacing: "-0.01em",
      display: "block",
    }}
  >
    {children}
  </Editable>
);

const MemberStory = (_props: ReportHostedSectionProps) => (
  <HostedReportSectionLayout
    sectionDomId="member-story"
    className="report-hosted-section-body"
    style={{ background: "var(--reema-sand-100)", fontFamily: body }}
    innerClassName="max-w-6xl mx-auto"
    banner={
      <SectionBanner
        title="Member Spotlight"
        pb={40}
        icon={<Sparkles className="banner__icon" aria-hidden="true" style={{ color: accent }} />}
      />
    }
  >

      <SectionReveal delay={0.1}>
        <div className="member-spotlight-layout grid md:grid-cols-[minmax(0,400px)_minmax(0,1fr)] gap-8 md:gap-10 items-start">
          {/* Left card — needs + ongoing priorities */}
          <div
            className="member-spotlight-profile-card rounded-2xl p-5 shadow-sm"
            style={{ background: "#fff", border: `1px solid ${border}` }}
          >
            <div className="flex items-center gap-3">
              <MemberIcon
                alt="Jessica"
                className="w-14 h-14 md:w-16 md:h-16 shrink-0"
              />
              <Editable
                noteKey="name"
                as="h3"
                style={{
                  fontFamily: display,
                  fontWeight: 600,
                  fontSize: 30,
                  color: foreground,
                  margin: 0,
                  lineHeight: 1.1,
                  letterSpacing: "-0.01em",
                  display: "block",
                }}
              >
                Jessica
              </Editable>
            </div>

            <hr className="my-5" style={{ border: 0, borderTop: `1px solid ${border}` }} />

            <div className="mb-6">
              <Eyebrow noteKey="needs:label">Needs</Eyebrow>
              <EditableBulletList items={needs} groupKey="needs" />
            </div>

            <div>
              <Eyebrow noteKey="ongoing:label">Ongoing Priorities</Eyebrow>
              <EditableBulletList items={ongoingPriorities} groupKey="ongoing" />
            </div>
          </div>

          {/* Narrative — fills the 2/3 column */}
          <div className="member-spotlight-narrative flex flex-col gap-5">
            <div>
              <SubHeading noteKey="summary:label">Summary</SubHeading>
              <Editable
                noteKey="summary:body"
                as="p"
                style={{
                  fontFamily: body,
                  fontSize: 15,
                  color: muted,
                  margin: 0,
                  lineHeight: 1.6,
                  display: "block",
                }}
              >
                Jessica | 29-year-old domestic violence survivor and single parent navigating housing
                instability, financial strain, and divorce
              </Editable>
            </div>

            <div>
              <SubHeading noteKey="challenge:label">The Challenge</SubHeading>
              <EditableBulletList items={challenge} groupKey="challenge" />
            </div>

            <div>
              <SubHeading noteKey="howReema:label">How Reema Made the Difference</SubHeading>
              <EditableBulletList items={howReema} groupKey="howReema" />
            </div>

            <div>
              <SubHeading noteKey="outcomes:label">Outcomes</SubHeading>
              <EditableBulletList items={outcomes} groupKey="outcomes" />
            </div>
          </div>
        </div>
      </SectionReveal>
  </HostedReportSectionLayout>
);

export default MemberStory;
